
import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc =
    `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const PdfImageViewer = ({ title, pdfUrl, onClose }) => {
    const canvasRef = useRef(null);
    const pdfRef = useRef(null);
    const renderTaskRef = useRef(null);

    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Load PDF
    useEffect(() => {
        let cancelled = false;
        let loadingTask;

        const loadPdf = async () => {
            try {
                setLoading(true);
                setError("");
                setPageNumber(1);
                setTotalPages(0);
                pdfRef.current = null;

                loadingTask = pdfjsLib.getDocument({
                    url: pdfUrl,
                });

                const pdf = await loadingTask.promise;

                if (cancelled) return;

                pdfRef.current = pdf;
                setTotalPages(pdf.numPages);
            } catch (err) {
                if (!cancelled) {
                    console.error("PDF Loading Error:", err);
                    setError("Unable to load PDF");
                    setLoading(false);
                }
            }
        };

        loadPdf();

        return () => {
            cancelled = true;
            renderTaskRef.current?.cancel();
            loadingTask?.destroy();
            pdfRef.current = null;
        };
    }, [pdfUrl]);

    // Convert PDF page to Canvas
    useEffect(() => {
        if (!pdfRef.current || !canvasRef.current) return;

        let cancelled = false;

        const renderPage = async () => {
            try {
                setLoading(true);
                setError("");

                if (renderTaskRef.current) {
                    renderTaskRef.current.cancel();

                    try {
                        await renderTaskRef.current.promise;
                    } catch (e) {
                        // Previous render cancelled
                    }
                }

                const page = await pdfRef.current.getPage(pageNumber);

                if (cancelled) return;

                const canvas = canvasRef.current;
                const context = canvas.getContext("2d");

                const viewport = page.getViewport({ scale: 1.5 });

                canvas.width = viewport.width;
                canvas.height = viewport.height;

                const renderTask = page.render({
                    canvasContext: context,
                    viewport,
                });

                renderTaskRef.current = renderTask;

                await renderTask.promise;

                if (!cancelled) {
                    setLoading(false);
                }
            } catch (err) {
                if (!cancelled && err.name !== "RenderingCancelledException") {
                    console.error("PDF Render Error:", err);
                    setError("Unable to display PDF page");
                    setLoading(false);
                }
            }
        };

        renderPage();

        return () => {
            cancelled = true;
            renderTaskRef.current?.cancel();
        };
    }, [pageNumber, totalPages]);

    const handleNext = () => {
        if (pageNumber < totalPages) {
            setPageNumber((prev) => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (pageNumber > 1) {
            setPageNumber((prev) => prev - 1);
        }
    };

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.7)",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "10px",
            }}
        >
            <div
                style={{
                    width: "100%",
                    maxWidth: "1000px",
                    height: "95vh",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        backgroundColor: "#0d6efd",
                        color: "#fff",
                    }}
                >
                    <span style={{ fontSize: "16px", fontWeight: "600" }}>
                        Special TET - 2026 Answer Key (
                        <b
                            style={{
                                // backgroundColor: "#fff3cd",
                                // color: "#856404",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontWeight: "750",
                                display: "inline-block",
                            }}
                        >
                            {title}
                        </b>
                        )
                    </span>

                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            background: "transparent",
                            border: "none",
                            color: "#fff",
                            fontSize: "24px",
                            cursor: "pointer",
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* PDF Page */}
                <div
                    style={{
                        flex: 1,
                        overflow: "auto",
                        backgroundColor: "#e9ecef",
                        padding: "15px",
                        textAlign: "center",
                    }}
                    onContextMenu={(e) => e.preventDefault()}
                >
                    {loading && (
                        <p style={{ color: "#333" }}>
                            Loading PDF page...
                        </p>
                    )}

                    {error && (
                        <p style={{ color: "red" }}>
                            {error}
                        </p>
                    )}

                    <canvas
                        ref={canvasRef}
                        style={{
                            display: loading || error ? "none" : "block",
                            maxWidth: "100%",
                            height: "auto",
                            margin: "0 auto",
                            backgroundColor: "#fff",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                            userSelect: "none",
                        }}
                    />
                </div>

                {/* Footer Navigation */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "20px",
                        padding: "12px",
                        backgroundColor: "#fff",
                        borderTop: "1px solid #ddd",
                    }}
                >
                    <button
                        type="button"
                        onClick={handlePrevious}
                        disabled={pageNumber === 1 || loading}
                        style={{
                            padding: "8px 18px",
                            backgroundColor:
                                pageNumber === 1 || loading ? "#adb5bd" : "#0d6efd",
                            color: "#fff",
                            border: "none",
                            borderRadius: "5px",
                            cursor:
                                pageNumber === 1 || loading ? "not-allowed" : "pointer",
                        }}
                    >
                        Previous
                    </button>

                    <span
                        style={{
                            fontWeight: "600",
                            fontSize: "14px",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {pageNumber} / {totalPages}
                    </span>

                    <button
                        type="button"
                        onClick={handleNext}
                        disabled={pageNumber === totalPages || loading}
                        style={{
                            padding: "8px 18px",
                            backgroundColor:
                                pageNumber === totalPages || loading
                                    ? "#adb5bd"
                                    : "#0d6efd",
                            color: "#fff",
                            border: "none",
                            borderRadius: "5px",
                            cursor:
                                pageNumber === totalPages || loading
                                    ? "not-allowed"
                                    : "pointer",
                        }}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PdfImageViewer;
